import {
  CabinetRange,
  CabinetSupport,
  CrossPieceType,
  MIN_WIDTH_NEED_REINFORCE,
  PlateType,
  Position,
  PositionAngle,
} from '../constants';
import { Cabinet } from '../entities/Cabinet';

import { Top } from '../entities/Top';
import { Rack } from '../entities/Rack';
import {
  battenFactory,
  cornerFactory,
  crossPieceFactory,
  intermediateShelFactory,
  paneFactory,
  plateFactory,
  supportIntermediateShelFactory,
} from './componentsFactories';
import {
  shelfFactory,
  shelfWithOneDrawerFactory,
  shelfWithOneIntermediateShelfFactory,
} from './shelvesFactories';
import { Color } from '../entities/Color';
import { CompleteIntermediateShelf } from '../entities/CompleteIntermediateShelf';
import { setColors } from '../utils/dataStorage';
import colorsData from './fixtures/colors.json';
import { Furniture } from '../entities/Furniture';
import { checkAndUpdate } from '../builder/change';
import { Plate } from '../pieces/Plate';
import { findPlate } from '../utils/piecesUpdater';

const colors = setColors(Color.setupColors(colorsData));
const whiteColor = colors.find(item => item.reference === 'W4006');

export function rackFactory(
  level = 0,
  height = 370,
  width = 1000,
  depth = 420,
): Rack {
  const battens = [
    battenFactory(PositionAngle.FrontLeft, height),
    battenFactory(PositionAngle.FrontRight, height),
    battenFactory(PositionAngle.BackLeft, height),
    battenFactory(PositionAngle.BackRight, height),
  ];
  const crossPieces = [
    crossPieceFactory(Position.Back, width, CrossPieceType.UpperPaneLowerPane),
    crossPieceFactory(Position.Left, depth, CrossPieceType.UpperPaneLowerPane),
    crossPieceFactory(Position.Right, depth, CrossPieceType.UpperPaneLowerPane),
  ];
  if (level === 0) {
    crossPieces.push(
      crossPieceFactory(
        Position.Front,
        width,
        CrossPieceType.UpperDoorsLowerDoors,
        undefined,
        width >= MIN_WIDTH_NEED_REINFORCE,
      ),
    );
  }

  return new Rack(
    level,
    height,
    crossPieces,
    [
      paneFactory(Position.Back, height, width),
      paneFactory(Position.Left, height, depth),
      paneFactory(Position.Right, height, depth),
    ],
    battens,
    plateFactory(Position.Front, depth, width),
  );
}

export function topFactory(width = 1000, depth = 420) {
  const plate = findPlate(
    Plate.standardPlate(420, 1000, Position.Front),
    depth,
    width,
    undefined,
    undefined,
    width >= 620 ? PlateType.DoorsPane : PlateType.PanePane,
  );

  return new Top(
    [
      crossPieceFactory(
        Position.Front,
        width,
        CrossPieceType.UpperEmptyLowerDoors,
      ),
      crossPieceFactory(
        Position.Back,
        width,
        CrossPieceType.UpperEmptyLowerPane,
      ),
      crossPieceFactory(
        Position.Left,
        depth,
        CrossPieceType.UpperEmptyLowerPane,
      ),
      crossPieceFactory(
        Position.Right,
        depth,
        CrossPieceType.UpperEmptyLowerPane,
      ),
    ],
    plate,
  );
}

export function cabinetFactory(
  width = 1000,
  depth = 420,
  range = CabinetRange.Standard,
  support = CabinetSupport.NoSupport,
): Cabinet {
  const cabinet = new Cabinet(
    width,
    depth,
    range,
    [
      cornerFactory(PositionAngle.FrontLeft),
      cornerFactory(PositionAngle.FrontRight),
      cornerFactory(PositionAngle.BackLeft),
      cornerFactory(PositionAngle.BackRight),
    ],
    [],
    topFactory(width, depth),
  );
  cabinet.shelves.push(shelfFactory(0, 470, cabinet));
  cabinet.shelves[0].height = 370;
  checkAndUpdate(cabinet);

  return cabinet;
}

export function furnitureFactory(): Furniture {
  const furniture = new Furniture();
  furniture.add(cabinetFactory());
  furniture.add(cabinetFactory());

  return furniture;
}

export function cabinetWithDrawerFactory(
  width = 1000,
  depth = 420,
  range = CabinetRange.Standard,
  support = CabinetSupport.NoSupport,
): Cabinet {
  const cabinet = new Cabinet(
    width,
    depth,
    range,
    [],
    [],
    topFactory(width, depth),
  );
  cabinet.shelves.push(shelfWithOneDrawerFactory(0, 370, cabinet));

  return cabinet;
}

export function cabinetWithIntermediateShelfFactory(
  width = 1000,
  depth = 420,
  range = CabinetRange.Standard,
  support = CabinetSupport.NoSupport,
) {
  const cabinet = new Cabinet(
    width,
    depth,
    range,
    [],
    [],
    topFactory(width, depth),
  );
  cabinet.shelves.push(shelfWithOneIntermediateShelfFactory(0, 370, cabinet));

  return cabinet;
}

export function completeIntermediateShelfFactory(
  position: number,
  height: number,
  width: number,
  depth: number,
  color: Color = whiteColor as Color,
  supportcolor: Color = whiteColor as Color,
) {
  const supportLeft = supportIntermediateShelFactory(
    Position.Left,
    height,
    depth,
    supportcolor,
  );
  const supportRight = supportIntermediateShelFactory(
    Position.Right,
    height,
    depth,
    supportcolor,
  );
  return new CompleteIntermediateShelf(
    position,
    intermediateShelFactory(0, width, depth, color),
    [supportLeft, supportRight],
  );
}
